import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";

import { cn } from "../lib/utils";

/**
 * Chip — small inline pill used for status, filters, tags, counts.
 *
 * Growth Fusion theme (2026-05-23):
 *   - Quiet pills, NOT loud badges. 12px text, small radius (full).
 *   - Status tones are independent of brand. Use the named status
 *     variants (active / trial / overdue / archived) for customer
 *     status. Use brand for filter-pill active state.
 *   - All tones meet WCAG 2.2 AA contrast (≥4.5:1).
 *   - Font weight is medium (500), never 600/700.
 */
export const chipVariants = cva(
  "inline-flex items-center gap-1 rounded-[var(--radius-full)] border px-2 py-0.5 text-[12px] font-medium",
  {
    variants: {
      tone: {
        /* Neutral default — labels, metadata, low-priority tags */
        default:  "bg-[var(--color-surface-soft)] border-[var(--color-border-hairline)] text-[var(--color-text-soft)]",

        /* Brand — teal-tinted, used for active filter pills */
        brand:    "bg-[var(--color-brand-soft)] border-transparent text-[var(--color-brand-strong)]",

        /* Status — independent of brand, consistent meaning */
        active:   "bg-[var(--color-status-active-bg)]   border-transparent text-[var(--color-status-active-text)]",
        trial:    "bg-[var(--color-status-trial-bg)]    border-transparent text-[var(--color-status-trial-text)]",
        overdue:  "bg-[var(--color-status-overdue-bg)]  border-transparent text-[var(--color-status-overdue-text)]",
        archived: "bg-[var(--color-status-archived-bg)] border-transparent text-[var(--color-status-archived-text)]",

        /* Legacy aliases — kept so existing call sites still compile.
           green → active, amber → trial, red → overdue, blue → brand. */
        green:    "bg-[var(--color-status-active-bg)]   border-transparent text-[var(--color-status-active-text)]",
        amber:    "bg-[var(--color-status-trial-bg)]    border-transparent text-[var(--color-status-trial-text)]",
        red:      "bg-[var(--color-status-overdue-bg)]  border-transparent text-[var(--color-status-overdue-text)]",
        blue:     "bg-[var(--color-brand-soft)]         border-transparent text-[var(--color-brand-strong)]",
      },
    },
    defaultVariants: { tone: "default" },
  },
);

export interface ChipProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof chipVariants> {}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  ({ className, tone, ...props }, ref) => (
    <span ref={ref} className={cn(chipVariants({ tone, className }))} {...props} />
  ),
);
Chip.displayName = "Chip";
